import Link from '@components/common/Link/Link'
import FishLogo from '@components/icons/FishLogo'
import Button from '@components/ui/ButtonV2/Button'
import FormControl from '@components/ui/inputs/FormControl'
import TextInput from '@components/ui/inputs/TextInput/TextInput'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCustomErrors } from '@lib/hooks/useCustomErrors'
import clsx from 'clsx'

import {
  customerCreateTokenFormSchema,
  CustomerCreateTokenFormValues,
} from '@features/account/api/customer-create-token'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/router'
import { FC, useState } from 'react'
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form'
import { ImSpinner2 } from 'react-icons/im'
import s from './M56LoginForm.module.scss'

export type LoginFormProps = {
  className?: string
}

export const M56LoginForm: FC<LoginFormProps> = ({ className, ...props }) => {
  const router = useRouter()

  const methods = useForm<CustomerCreateTokenFormValues>({
    resolver: zodResolver(customerCreateTokenFormSchema),
    defaultValues: {
      email: router.query?.email as string | undefined,
    },
    mode: 'onBlur',
  })

  const email = methods.watch('email')

  const { addError, loading, customErrors, clearErrors } = useCustomErrors()

  const [loadingSpinner, setLoadingSpinner] = useState(false)

  const loginAction: SubmitHandler<CustomerCreateTokenFormValues> = async (
    data,
  ) => {
    setLoadingSpinner(true)

    const response = await signIn('credentials', {
      redirect: false,
      email: data.email,
      password: data.password,
    })

    if (!response.ok) {
      setLoadingSpinner(false)

      addError({
        code: 'invalidCredentials',
        message: 'Wrong email or password',
      })
    } else {
      clearErrors()

      const callbackUrl = new URL(response.url).searchParams.get('callbackUrl')

      if (callbackUrl != null && callbackUrl != '') {
        router.push(decodeURIComponent(callbackUrl))
      } else {
        router.push(
          `/${router.query.language}/${router.query.country}/account/dashboard`,
        )
      }
    }
  }

  return (
    <div className={clsx('container', s.root, className)}>
      <div className={s.headline}>LOGIN</div>

      <FormProvider {...methods}>
        <form className={s.form} onSubmit={methods.handleSubmit(loginAction)}>
          {customErrors && customErrors.length > 0 && (
            <div className={s.errorContainer}>
              {customErrors.map((error) => (
                <div key={error.code} className={s.error}>
                  {error.message}
                </div>
              ))}
            </div>
          )}
          <div className={s.inputWrapper}>
            <Controller
              control={methods.control}
              name="email"
              render={({ field, fieldState }) => (
                <FormControl required floatingLabel>
                  <FormControl.Label>Email</FormControl.Label>
                  <TextInput className="w-full" type="email" {...field} />
                  {fieldState.error && (
                    <FormControl.Validation variant="error">
                      {fieldState.error.message}
                    </FormControl.Validation>
                  )}
                </FormControl>
              )}
            />
            <div className={s.passwordWrapper}>
              <Controller
                control={methods.control}
                name="password"
                render={({ field, fieldState }) => (
                  <FormControl required floatingLabel>
                    <FormControl.Label>Password</FormControl.Label>
                    <TextInput className="w-full" type="password" {...field} />
                    {fieldState.error && (
                      <FormControl.Validation variant="error">
                        {fieldState.error.message}
                      </FormControl.Validation>
                    )}
                  </FormControl>
                )}
              />
              <Link
                link={{ url: '/account/forgot-password' }}
                query={email ? { email } : undefined}
                className={s.forgotPasswordLink}
              >
                Forgot your password?
              </Link>
            </div>
          </div>

          <div className={s.submitWrapper}>
            <Button
              className={s.button}
              variant="secondary"
              type="submit"
              disabled={loading || loadingSpinner}
            >
              {loading || loadingSpinner ? (
                <ImSpinner2 className={s['icon-spin']} />
              ) : (
                'Login'
              )}
            </Button>

            <span className={s.secondaryAction}>
              or{' '}
              <Link
                link={{ url: '/account/sign-up' }}
                query={email ? { email } : undefined}
              >
                sign up instead
              </Link>
            </span>
          </div>
        </form>
      </FormProvider>

      <FishLogo width={30} height={30} className="mx-auto" />
    </div>
  )
}

M56LoginForm.displayName = 'M56LoginForm'
export default M56LoginForm
